/**
 * CustomVariantPicker — custom element for the "custom_variant_picker" block.
 *
 * Reads a JSON data blob rendered by Liquid, groups option values by the part
 * BEFORE the first underscore (the "prefix"), and surfaces the parts AFTER
 * the underscore as secondary "suboption" pill buttons.
 *
 * e.g. option values:
 *   "H2C AMS Combo_Standard"          → prefix "H2C AMS Combo", suboption "Standard"
 *   "H2C Laser Full Combo_10W Laser"  → prefix "H2C Laser Full Combo", suboption "10W Laser"
 *   "H2C Laser Full Combo_40W Laser"  → prefix "H2C Laser Full Combo", suboption "40W Laser"
 *
 * If an option value has NO underscore it is treated as a standalone card with
 * no suboptions panel.
 */

if (!customElements.get('custom-variant-picker')) {
  class CustomVariantPicker extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.productFormId = this.dataset.productFormId;

      // Parse server-rendered variant data
      const jsonEl = this.querySelector('[data-custom-variants]');
      if (!jsonEl) return;
      this.variantData = JSON.parse(jsonEl.textContent);

      // Find product slider references for media updates
      this.productWrapper = this.closest('.thb-product-detail');
      if (this.productWrapper) {
        this.productSlider =
          this.productWrapper.querySelector('.product-images') ||
          this.productWrapper.querySelector('.product-quick-images');
        this.thumbnails = this.productWrapper.querySelector('.product-thumbnail-container');
      }

      this._bindEvents();
    }

    // ─── Event binding ──────────────────────────────────────────────────────

    _bindEvents() {
      // Card clicks — delegate to the .cvp-card div inside each .cvp-item
      this.querySelectorAll('.cvp-card').forEach((card) => {
        card.addEventListener('click', () => this._onCardClick(card));
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._onCardClick(card);
          }
        });
      });

      // Suboption button clicks
      this.querySelectorAll('.cvp-suboption-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation(); // don't bubble to card
          this._onSuboptionClick(btn);
        });
      });
    }

    // ─── Card selection ─────────────────────────────────────────────────────

    _onCardClick(card) {
      const item = card.closest('.cvp-item');
      if (!item) return;

      // Already selected — nothing to do
      if (item.classList.contains('is-selected')) return;

      // Deselect all, select clicked
      this.querySelectorAll('.cvp-item').forEach((i) => i.classList.remove('is-selected'));
      item.classList.add('is-selected');

      // Auto-select first suboption in this item
      const firstBtn = item.querySelector('.cvp-suboption-btn');
      if (firstBtn) {
        item.querySelectorAll('.cvp-suboption-btn').forEach((b) => b.classList.remove('is-selected'));
        firstBtn.classList.add('is-selected');
      }

      this._applySelection(item);
    }

    // ─── Suboption selection ────────────────────────────────────────────────

    _onSuboptionClick(btn) {
      const item = btn.closest('.cvp-item');
      if (!item) return;

      item.querySelectorAll('.cvp-suboption-btn').forEach((b) => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      this._applySelection(item);
    }

    // ─── Core: resolve variant & propagate ─────────────────────────────────

    _applySelection(item) {
      const prefix = item.dataset.prefix;
      const selectedBtn = item.querySelector('.cvp-suboption-btn.is-selected');
      const suboption = selectedBtn ? selectedBtn.dataset.suboption : null;

      // Build the full option1 value the same way Liquid does
      const option1 = suboption ? `${prefix}_${suboption}` : prefix;

      const variant = this.variantData.variants.find((v) => v.option1 === option1);
      if (!variant) return;

      // Update the price shown inside the selected card
      this._updateCardPrice(item, variant);

      // Update hidden form input (what actually goes into the cart)
      this._updateVariantInput(variant.id);

      // Update product image carousel
      if (variant.featured_media_id) {
        this._updateMedia(variant.featured_media_id);
      }

      // Update shipping / legal text inside this item
      this._updateShippingLegal(item, variant);

      // Enable / disable add-to-cart button
      this._toggleAddButton(!variant.available);

      // Fetch server-rendered price / inventory sections
      this._fetchVariantSections(variant.id);

      // Notify the rest of the page (sticky bar, etc.)
      if (typeof dispatchCustomEvent === 'function') {
        dispatchCustomEvent('product:variant-change', {
          variant: { id: variant.id, available: variant.available },
          sectionId: this.sectionId,
        });
      }
    }

    // ─── Card price ──────────────────────────────────────────────────────────

    _updateCardPrice(item, variant) {
      const priceEl = item.querySelector('.cvp-card__price');
      if (!priceEl || !variant.price_formatted) return;
      const currency = variant.currency ? ` ${variant.currency}` : '';
      priceEl.textContent = `${variant.price_formatted}${currency}`;
    }

    // ─── Form input ─────────────────────────────────────────────────────────

    _updateVariantInput(variantId) {
      const forms = document.querySelectorAll(
        `#product-form-${this.sectionId}, #product-form-installment`
      );
      forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (input) {
          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    // ─── Media / carousel ───────────────────────────────────────────────────

    _updateMedia(mediaId) {
      if (!this.productSlider) return;

      const mediaSelector = `#Slide-${this.sectionId}-${mediaId}`;
      const thumbSelector = `#Thumb-${this.sectionId}-${mediaId}`;

      if (this.thumbnails) {
        // Flickity-based slider with thumbnails
        if (typeof Flickity !== 'undefined') {
          const flkty = Flickity.data(this.productSlider);
          if (flkty) {
            this.productSlider.selectCell(mediaSelector);
          }
        }
        // Also update thumb active state
        const activeThumb = this.thumbnails.querySelector(thumbSelector);
        if (activeThumb) {
          this.thumbnails.querySelectorAll('.product-thumbnail').forEach((t) =>
            t.classList.remove('is-active', 'is-initial-selected')
          );
          activeThumb.classList.add('is-active', 'is-initial-selected');
        }
      } else {
        // Grid / stacked layout
        const activeMedia = this.productSlider.querySelector(mediaSelector);
        if (activeMedia) {
          this.productSlider.querySelectorAll('[data-media-id]').forEach((el) =>
            el.classList.remove('is-active')
          );
          activeMedia.classList.add('is-active');
          activeMedia.parentElement.prepend(activeMedia);
        }
      }
    }

    // ─── Shipping / legal text ───────────────────────────────────────────────

    _updateShippingLegal(item, variant) {
      const legalEl = item.querySelector('.cvp-shipping-legal');
      if (!legalEl) return;

      const text = variant.shipping_legal || '';
      legalEl.textContent = text;

      if (text) {
        legalEl.removeAttribute('hidden');
      } else {
        legalEl.setAttribute('hidden', '');
      }
    }

    // ─── Price / inventory fetch ─────────────────────────────────────────────

    _fetchVariantSections(variantId) {
      const url = `${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`;

      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');

          const sectionIds = [
            `price-${this.sectionId}`,
            `price-${this.sectionId}--sticky`,
            `inventory-${this.sectionId}`,
            `sku-${this.sectionId}`,
            `quantity-${this.sectionId}`,
            `product-image-${this.sectionId}--sticky`,
          ];

          sectionIds.forEach((id) => {
            const dest = document.getElementById(id);
            const src = doc.getElementById(id);
            if (dest && src) {
              dest.innerHTML = src.innerHTML;
              if (id.includes('price')) dest.classList.remove('visibility-hidden');
            }
          });
        })
        .catch(() => {
          // Silently ignore fetch errors
        });
    }

    // ─── Add-to-cart button state ────────────────────────────────────────────

    _toggleAddButton(disable) {
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
      submitButtons.forEach((btn) => {
        const textEl = btn.querySelector('.single-add-to-cart-button--text');
        if (disable) {
          btn.setAttribute('disabled', 'disabled');
          if (textEl) {
            textEl.textContent =
              window.theme?.variantStrings?.soldOut || 'Sold out';
          }
        } else {
          btn.removeAttribute('disabled');
          btn.classList.remove('loading');
          if (textEl) {
            textEl.textContent =
              window.theme?.variantStrings?.addToCart || 'Add to cart';
          }
        }
      });
    }
  }

  customElements.define('custom-variant-picker', CustomVariantPicker);
}
